import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';

import { TranslationKeyMap } from '../focus-selector.i18n';
import { Row } from '../grid/experimental.grid';
import { GridSortClickEvent } from '../grid/reusable-grid.model';
import {
    FocusHistorySelectRowEvent,
    FocusHistorySortGridEvent,
} from '../model/focus-history-event.model';
import NavigationTabRefs from '../model/focus-history-navigation-tab-refs.enum';
import { FocusHistoryGrid } from '../model/focus-history.grid.model';
import { FocusSelectorBehaviourModel } from '../model/focus-selector-behaviour.model';
import { FocusSelectorFocusForPerformer } from '../model/focus-selector-focus-for-performer.model';
import { FocusSelectorFocusForTeam } from '../model/focus-selector-focus-for-team.model';
import { FocusSelectorSelectFocusEvent } from '../model/focus-selector-select-focus-event.model';

@Component({
    selector: 'cmps-focus-history',
    templateUrl: './focus-history.component.html',
    host: { class: 'cmps-focus-history' },
})
export class FocusHistoryComponent implements OnChanges {
    @Input() pending: boolean;
    @Input() behaviours: FocusSelectorBehaviourModel[];
    @Input() activeFocusesForPerformer: FocusSelectorFocusForPerformer[];

    @Input()
    activeForPerformerGrid: FocusHistoryGrid<FocusSelectorFocusForPerformer>;

    @Input()
    closedForPerformerGrid: FocusHistoryGrid<FocusSelectorFocusForPerformer>;

    @Input()
    activeForTeamGrid: FocusHistoryGrid<FocusSelectorFocusForTeam>;

    @Input()
    closedForTeamGrid: FocusHistoryGrid<FocusSelectorFocusForTeam>;

    @Output()
    selectGridRow: EventEmitter<FocusHistorySelectRowEvent>;

    @Output() sortGrid: EventEmitter<FocusHistorySortGridEvent>;
    @Output() clearGridSelection: EventEmitter<NavigationTabRefs>;
    @Output() selectFocus: EventEmitter<FocusSelectorSelectFocusEvent>;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _NavigationTabRefs = NavigationTabRefs;
    /** @private */ _selectedFocus: FocusHistoryGrid<
        FocusSelectorFocusForPerformer | FocusSelectorFocusForTeam
    >['selection'];

    private _currentTab: NavigationTabRefs;

    constructor() {
        this._currentTab = NavigationTabRefs.ACTIVE_FOR_PERFORMER;
        this._selectedFocus = null;
        this.selectGridRow = new EventEmitter();
        this.sortGrid = new EventEmitter();
        this.clearGridSelection = new EventEmitter();
        this.selectFocus = new EventEmitter();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.activeForPerformerGrid != null) {
            this._selectedFocus = this.activeForPerformerGrid.selection ?? null;
        }

        if (changes.closedForPerformerGrid != null) {
            this._selectedFocus = this.closedForPerformerGrid.selection ?? null;
        }

        if (changes.activeForTeamGrid != null) {
            this._selectedFocus = this.activeForTeamGrid.selection ?? null;
        }

        if (changes.closedForTeamGrid != null) {
            this._selectedFocus = this.closedForTeamGrid.selection ?? null;
        }
    }

    /** @private */ _selectGridRow(
        gridTab: NavigationTabRefs,
        rowId: Row['id'],
    ): void {
        this.selectGridRow.emit({ gridTab, rowId });
    }

    /** @private */ _sortGrid(
        gridTab: NavigationTabRefs,
        gridSortEvent: GridSortClickEvent,
    ): void {
        this.sortGrid.emit({ gridTab, gridSortEvent });
    }

    /** @private */ _handleTabChange($event: NavigationTabRefs): void {
        this.clearGridSelection.emit(this._currentTab);
        this._currentTab = $event;
    }

    /** @private */ _selectFocus(): void {
        if (this._selectedFocus != null) {
            const activeCommitmentId = this._getActiveCommitmentId(
                this._selectedFocus.behaviourId,
                this._selectedFocus.tacticId,
            );

            this.selectFocus.emit({
                behaviour: {
                    id: this._selectedFocus.behaviourId,
                    name: this._selectedFocus.behaviour,
                },
                subBehaviour: {
                    id: this._selectedFocus.tacticId,
                    name: this._selectedFocus.tactic,
                },
                activeCommitmentId,
            });
        }
    }

    private _getActiveCommitmentId(
        behaviourId: number,
        tacticId: number,
    ): FocusSelectorFocusForPerformer['commitmentId'] {
        const foundActiveFocus = this.activeFocusesForPerformer.find(
            (focus) =>
                focus.behaviourId === behaviourId &&
                focus.tacticId === tacticId,
        );

        return foundActiveFocus?.commitmentId ?? null;
    }
}
